import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { join, orderBy, map, isEqual } from 'lodash';
import { Link, Redirect } from 'react-router-dom';
import { Table, Alert, Button } from 'react-bootstrap';
import { Meteor } from 'meteor/meteor';
import { Roles } from 'meteor/alanning:roles';
import { withTracker } from 'meteor/react-meteor-data';
import { Bert } from 'meteor/themeteorchef:bert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  getUserName,
  isEmailVerified,
  getEmailAddress,
  getOauthService,
  isOauthUser
} from '../../../api/Users/user-operations';

import { monthDayYearAtTime } from '../../../modules/dates';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import PageHeader from '../../atoms/PageHeader';
import TableButtonCell from '../../atoms/TableButtonCell';
import ItemList from '../../components/ItemList';
import Loading from '../../components/Loading';
import Trash from '../../components/Trash';

const SORT_OPTIONS = { name: 'profile.name.first', createdAt: 'createdAt' };

const ASCENDENT = 'asc';
const DESCENDENT = 'desc';

const OAuthIcon = ({ service }) => {
  if (service === 'esri') {
    return <FontAwesomeIcon icon="globe" />;
  }
  return <FontAwesomeIcon icon={['fab', service]} />;
};
OAuthIcon.propTypes = {
  service: PropTypes.string.isRequired
};

const UserEmailCell = ({ user, isOAuthUser, goToUser }) => {
  if (isOAuthUser) {
    const userService = getOauthService(user);
    return (
      <td role="gridcell" onClick={goToUser} className="OAuthCell align-middle">
        <div className={`LoggedInWith ${userService}`}>
          <div className="ServiceIcon">
            <OAuthIcon service={userService} />
          </div>
          <p>{getEmailAddress(user)}</p>
        </div>
      </td>
    );
  }
  return (
    <td
      role="gridcell"
      className={`align-middle ${!isEmailVerified(user) && 'warning'}`}
      onClick={goToUser}
    >
      {getEmailAddress(user)}
    </td>
  );
};
UserEmailCell.propTypes = {
  user: PropTypes.object.isRequired,
  goToUser: PropTypes.func.isRequired,
  isOAuthUser: PropTypes.bool.isRequired
};

const UserRow = ({ user, currentUserId, history, match, handleSoftRemove }) => {
  const userRoles = join(user.roles, ', ');
  const isThisCurrentUser = currentUserId === user._id;
  let goToUser = () => {
    console.error('user cannot edit himself in manager');
  };
  if (!isThisCurrentUser) {
    goToUser = () => (!isThisCurrentUser ? history.push(`${match.url}/${user._id}/edit`) : '');
  }
  return (
    <tr key={user._id}>
      <td role="gridcell" className="align-middle" onClick={goToUser}>
        {getUserName(user)}
      </td>
      <UserEmailCell user={user} isOAuthUser={isOauthUser(user)} goToUser={goToUser} />
      <td role="gridcell" className="align-middle" onClick={goToUser}>
        {userRoles}
      </td>
      <td role="gridcell" className="align-middle" onClick={goToUser}>
        {monthDayYearAtTime(user.createdAt)}
      </td>
      <TableButtonCell>
        <Button
          variant="danger"
          onClick={() => handleSoftRemove(user._id)}
          disabled={isThisCurrentUser}
        >
          <FontAwesomeIcon icon="times" />
        </Button>
      </TableButtonCell>
    </tr>
  );
};
UserRow.propTypes = {
  user: PropTypes.object.isRequired,
  currentUserId: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  handleSoftRemove: PropTypes.func.isRequired
};

class UserManagement extends Component {
  state = {
    trashShow: false,
    sort: { value: SORT_OPTIONS.name, order: ASCENDENT }
  };

  componentDidMount() {
    this.authorizeAccess();
  }

  componentDidUpdate() {
    this.authorizeAccess();
  }

  trashClose = () => {
    this.setState({
      trashShow: false
    });
  };

  handleHardRemove = async userId => {
    if (userId === Meteor.userId()) {
      Bert.alert("You can't delete your own account", 'danger');
    } else {
      try {
        const { deletedFiles, deletedFolders } = await methodPromise('users.hardDelete', userId);
        Bert.alert(`User deleted! ${deletedFiles} files and ${deletedFolders} folders`, 'danger');
      } catch (error) {
        handleErrorInClient(error);
      }
    }
  };

  handleRestore = async userId => {
    try {
      await methodPromise('users.restore', userId);
      Bert.alert('User Restored!', 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  handleSoftRemove = async userId => {
    if (userId === Meteor.userId()) {
      Bert.alert("You can't delete your own account", 'danger');
    } else {
      try {
        await methodPromise('users.softDelete', userId);
        Bert.alert('User moved to Trash!', 'warning');
      } catch (error) {
        handleErrorInClient(error);
      }
    }
  };

  handleSort = value => {
    const { sort } = this.state;
    let order = ASCENDENT;
    if (sort.value === value && sort.order === ASCENDENT) order = DESCENDENT;
    this.setState({ sort: { value, order } });
  };

  authorizeAccess() {
    const { userId } = this.props;
    if (!Roles.userIsInRole(userId, ['admin'])) {
      return <Redirect to="/" />;
    }
    return true;
  }

  render() {
    const {
      loading,
      users,
      match,
      userId: currentUserId,
      deletedCount,
      deletedUsers,
      totalCount,
      history
    } = this.props;
    const { trashShow, sort } = this.state;
    if (loading) {
      return <Loading />;
    }

    return (
      <>
        <Trash
          title="Recycle Bin"
          isTrashOpen={trashShow}
          closeTrash={this.trashClose}
          itemName="Users"
          deletedCount={deletedCount}
          restoreItem={this.handleRestore}
          deleteItem={this.handleHardRemove}
          deletedItems={deletedUsers}
        />
        <PageHeader>
          <h4>User Manager</h4>
          <Link className="btn btn-success ml-auto" to={`${match.url}/new`}>
            Add User
          </Link>
        </PageHeader>
        {users.length === 0 ? (
          <Alert variant="warning">Something went wrong!</Alert>
        ) : (
          <ItemList>
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th
                    className="align-middle"
                    onClick={() => this.handleSort(SORT_OPTIONS.name)}
                    style={{ cursor: 'pointer' }}
                  >
                    Users ({totalCount}){' '}
                    {isEqual(sort.value, SORT_OPTIONS.name) && (
                      <>
                        {sort.order === ASCENDENT && (
                          <FontAwesomeIcon icon="sort-up" className="ml-1" />
                        )}
                        {sort.order === DESCENDENT && (
                          <FontAwesomeIcon icon="sort-down" className="ml-1" />
                        )}
                      </>
                    )}
                  </th>
                  <th className="align-middle">Email</th>
                  <th className="align-middle">Roles</th>
                  <th
                    className="align-middle"
                    onClick={() => this.handleSort(SORT_OPTIONS.createdAt)}
                    style={{ cursor: 'pointer' }}
                  >
                    Created{' '}
                    {isEqual(sort.value, SORT_OPTIONS.createdAt) && (
                      <>
                        {sort.order === ASCENDENT && (
                          <FontAwesomeIcon icon="sort-up" className="ml-1" />
                        )}
                        {sort.order === DESCENDENT && (
                          <FontAwesomeIcon icon="sort-down" className="ml-1" />
                        )}
                      </>
                    )}
                  </th>
                  <th className="align-middle text-center">
                    <Button
                      variant="light"
                      onClick={() => this.setState({ trashShow: true })}
                      block
                    >
                      <FontAwesomeIcon icon="trash-alt" />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {map(orderBy(users, [sort.value], [sort.order]), user => (
                  <UserRow
                    key={user._id}
                    user={user}
                    currentUserId={currentUserId}
                    history={history}
                    match={match}
                    handleSoftRemove={this.handleSoftRemove}
                  />
                ))}
              </tbody>
            </Table>
          </ItemList>
        )}
      </>
    );
  }
}

UserManagement.propTypes = {
  loading: PropTypes.bool.isRequired,
  userId: PropTypes.string.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  deletedUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  deletedCount: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired
};

export default withTracker(() => {
  const usersSub = Meteor.subscribe('users.management');
  return {
    loading: !usersSub.ready(),
    users: Meteor.users.find({ deleted: { $eq: 'no' } }).fetch(),
    deletedUsers: Meteor.users.find({ deleted: { $ne: 'no' } }).fetch(),
    deletedCount: Meteor.users.find({ deleted: { $ne: 'no' } }).count(),
    totalCount: Meteor.users.find({ deleted: { $eq: 'no' } }).count()
  };
})(UserManagement);
